// Transform WordPress REST API responses to match our expected format
import { Goal } from '../State/GoalsContext';
import { Indicator } from '../State/IndicatorsContext';
import { Target } from '../State/TargetsContext';
import { resolveIsBarChart, chartTypeFromBarFlag, dataDisplayTypeFromBarFlag } from './chartTypeUtils';
import { decodeHtmlEntities, indicatorTitleWithCode, stripLeadingIndicatorCode } from './displayText';

// WordPress REST API response format
interface WPGoalResponse {
  id: number;
  title: { rendered: string } | string;
  acf?: any;
  meta?: any;
  featured_media?: number;
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url?: string;
      [key: string]: any;
    }>;
    [key: string]: any;
  };
  [key: string]: any;
}

interface WPIndicatorResponse {
  id: number;
  title: { rendered: string } | string;
  acf?: any;
  meta?: any;
  featured_media?: number;
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url?: string;
      [key: string]: any;
    }>;
    [key: string]: any;
  };
  [key: string]: any;
}

interface WPTargetResponse {
  id: number;
  title: { rendered: string } | string;
  acf?: any;
  meta?: any;
  featured_media?: number;
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url?: string;
      [key: string]: any;
    }>;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Get title string from WordPress REST API title field
 */
function getTitleString(title: { rendered: string } | string): string {
  if (typeof title === 'string') return title;
  return title?.rendered || '';
}

/**
 * Transform WordPress REST API goal response to our Goal format
 */
export function transformGoal(wpGoal: WPGoalResponse): Goal {
  const title = getTitleString(wpGoal.title);
  // Handle case where acf might be empty array or missing
  const acf = (wpGoal.acf && typeof wpGoal.acf === 'object' && !Array.isArray(wpGoal.acf)) ? wpGoal.acf : {};
  
  // Extract title_render from ACF meta or generate from title
  const title_render = wpGoal.meta?.title_render || acf.title_render || stripIndexFromTitle(title);
  
  // Get featured image URL from _embedded if available
  const featuredImageUrl = wpGoal._embedded?.['wp:featuredmedia']?.[0]?.source_url || 
                           acf.featured_image_url || 
                           '';
  
  // Ensure ACF has required fields with defaults
  const safeAcf = {
    ...acf,
    status: acf.status ?? wpGoal.meta?.status ?? '0',
    alignment_with_sdg_data: acf.alignment_with_sdg_data ?? {
      status: wpGoal.meta?.alignment_with_sdg_data ?? '0'
    },
    name: acf.name || title_render || title,
    description: acf.description || '',
    goal_color: acf.goal_color || '',
    icon: acf.icon || '',
    index: acf.index || '',
  };
  
  return {
    id: wpGoal.id,
    title: title,
    title_render: title_render,
    acf: safeAcf,
    computed: {
      goal_semaphore_status: wpGoal.meta?.goal_semaphore_status || acf.goal_semaphore_status,
      goal_trend_status: wpGoal.meta?.goal_trend_status || acf.goal_trend_status,
      goal_index_score: wpGoal.meta?.goal_index_score !== undefined ? 
        (wpGoal.meta.goal_index_score === '' ? null : parseFloat(wpGoal.meta.goal_index_score)) : 
        acf.goal_index_score,
    },
    color: safeAcf.goal_color || '',
    icon: safeAcf.icon || '',
    featured_image_url: featuredImageUrl,
  };
}

/**
 * Transform WordPress REST API indicator response to our Indicator format
 */
export function transformIndicator(wpIndicator: WPIndicatorResponse): Indicator {
  const title = getTitleString(wpIndicator.title);
  // Handle case where acf might be empty array or missing
  const acf = (wpIndicator.acf && typeof wpIndicator.acf === 'object' && !Array.isArray(wpIndicator.acf)) ? wpIndicator.acf : {};
  
  const title_render = wpIndicator.meta?.title_render || acf.title_render || stripIndexFromTitle(title);
  
  // Extract goal_id and target_id from ACF relationships
  const targetRaw = acf.select_targets;
  const target_id = Array.isArray(targetRaw) ? (targetRaw[0] || null) : (targetRaw || null);
  
  // Goal ID would need to be fetched from target's select_goal, but for now we'll get it from acf if available
  const goal_id = acf.goal_id || null;
  
  const featuredImageUrl = wpIndicator._embedded?.['wp:featuredmedia']?.[0]?.source_url || 
                           acf.featured_image_url || 
                           '';
  
  // Ensure ACF has required fields with defaults
  const safeAcf = {
    ...acf,
    index: acf.index || '',
    status: acf.status ?? wpIndicator.meta?.status ?? '0',
    description: acf.description || '',
    reference_field: acf.reference_field || '',
    source: acf.source || '',
    graph_data_indicators: acf.graph_data_indicators || {},
    data_display_type: acf.data_display_type || 'line-graph-bar-chart',
  };
  
  return {
    id: wpIndicator.id,
    title: title,
    title_render: title_render,
    goal_id: goal_id,
    target_id: target_id,
    acf: safeAcf,
    computed: {
      score: wpIndicator.meta?.score !== undefined ? wpIndicator.meta.score : (acf.score || null),
      trend: wpIndicator.meta?.trend || acf.trend || null,
      scores_by_series: acf.scores_by_series || {},
      semaphore_status: wpIndicator.meta?.semaphore_status || acf.semaphore_status,
      trend_status: wpIndicator.meta?.trend_status || acf.trend_status,
    },
    featured_image_url: featuredImageUrl,
  };
}

/**
 * Transform WordPress REST API target response to our Target format
 */
export function transformTarget(wpTarget: WPTargetResponse): Target {
  const title = getTitleString(wpTarget.title);
  // Handle case where acf might be empty array or missing
  const acf = (wpTarget.acf && typeof wpTarget.acf === 'object' && !Array.isArray(wpTarget.acf)) ? wpTarget.acf : {};
  
  const title_render = wpTarget.meta?.title_render || acf.title_render || stripIndexFromTitle(title);
  
  const goalRaw = acf.select_goal;
  const goal_ids = Array.isArray(goalRaw) ? goalRaw : (goalRaw ? [goalRaw] : []);
  
  const featuredImageUrl = wpTarget._embedded?.['wp:featuredmedia']?.[0]?.source_url || 
                           acf.featured_image_url || 
                           '';
  
  // Ensure ACF has required fields with defaults
  const safeAcf = {
    ...acf,
    index: acf.index || '',
    status: acf.status ?? wpTarget.meta?.status ?? '0',
  };
  
  return {
    id: wpTarget.id,
    title: title,
    title_render: title_render,
    acf: safeAcf,
    computed: {
      target_semaphore_status: wpTarget.meta?.target_semaphore_status || acf.target_semaphore_status,
      target_trend_status: wpTarget.meta?.target_trend_status || acf.target_trend_status,
    },
    goal_ids: goal_ids,
    featured_image_url: featuredImageUrl,
  };
}

/**
 * Strip index from title (e.g., "1.1.1 Title" -> "Title")
 */
function stripIndexFromTitle(title: string): string {
  let clean = title.replace(/^\s*\d+(?:\.[0-9a-z]+)*\s+/i, '');
  // Goals titled like "1 : …" leave a leading ": …" after digit+space strip alone.
  clean = clean.replace(/^\s*:\s*/, '');
  return clean.trim();
}

// ===== PREVIEW ENDPOINT TRANSFORMERS =====
// These transform /sdg/v1/{entity}/{id}/preview responses

/**
 * Build chart payload from whole_data JSON string (API fallback when payload has no datasets).
 * Column 0 = Year (labels), Column 1 = calculation column (user-defined label, e.g. Total), Columns 2+ = disaggregations.
 */
export function buildChartDataFromWholeData(wholeData: string): { labels: string[]; datasets: Array<{ label: string; data: (number | null)[] }> } | null {
  if (!wholeData || typeof wholeData !== 'string' || !wholeData.trim()) return null;
  try {
    const obj = JSON.parse(wholeData);
    const columns = obj?.columns;
    if (!Array.isArray(columns) || columns.length < 2) return null;
    const labels = (columns[0]?.data ?? []).map((v: unknown) => (typeof v === 'number' ? String(v) : (v != null ? String(v) : '')));
    const datasets: Array<{ label: string; data: (number | null)[] }> = [];
    for (let i = 1; i < columns.length; i++) {
      const col = columns[i];
      const colLabel = (col?.label != null ? String(col.label) : `Series ${i}`).trim();
      const displayLabel = colLabel || 'Total';
      const data = (col?.data ?? []).map((v: unknown) =>
        typeof v === 'number' && Number.isFinite(v) ? v : (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : null)
      );
      datasets.push({ label: displayLabel, data });
    }
    return { labels, datasets };
  } catch {
    return null;
  }
}

/**
 * Transform goal preview response to our Goal format
 */
export function transformGoalPreview(preview: any): Goal {
  // Extract goal number from index if available, otherwise from title
  let goalIndex = preview.index || '';
  if (!goalIndex && preview.title) {
    // Fallback: extract from title (e.g., "Goal 1:" -> "1")
    const goalMatch = preview.title.match(/Goal\s+(\d+)/i);
    goalIndex = goalMatch ? goalMatch[1] : '';
  }
  const goalNumber = goalIndex.replace(/[^0-9]/g, ''); // Extract number from index
  // Prefer WP featured image; local bundled icons are resolved at render time as fallback
  const featuredFromApi = (
    preview.featured_image_url ||
    preview._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
    ''
  ).trim();
  
  return {
    id: preview.id,
    title: preview.title || '',
    title_render: preview.title_full || preview.title || '',
    acf: {
      index: goalNumber || goalIndex, // Use goalNumber (just the number) or fallback to goalIndex
      name: preview.title || '',
      description: preview.description || '',
      goal_color: preview.goal_color || '',
      status: '0',
      alignment_with_sdg_data: {
        status: '0'
      },
    },
    computed: {
      goal_semaphore_status: preview.assessment?.status?.category || null,
      goal_status_score: preview.assessment?.status?.mean_score_0_100 ?? null,
      goal_trend_status: preview.assessment?.trend?.category || null,
      goal_trend_score: preview.assessment?.trend?.mean_score_0_5 ?? null,
      goal_index_score: preview.assessment?.status?.mean_score_0_100 ?? null,
    },
    color: preview.goal_color || '',
    icon: goalNumber ? `sdg-${goalNumber}.svg` : '',
    featured_image_url: featuredFromApi,
  };
}

/**
 * Transform target preview response to our Target format
 */
export function transformTargetPreview(preview: any): Target {
  // Handle both preview endpoint (has goal object) and profile data (has goal_ids array)
  let goalIds: number[] = [];
  if (preview.goal_ids && Array.isArray(preview.goal_ids)) {
    goalIds = preview.goal_ids;
  } else if (preview.goal?.id) {
    goalIds = [preview.goal.id];
  } else {
    goalIds = [];
  }
  
  // Title from ACF (preview.title is now from ACF target_title field, not post_title)
  const title = preview.title || '';
  const titleFull = preview.title_full || (preview.index ? `${preview.index} · ${title}` : title);
  
  return {
    id: preview.id,
    title: title, // ACF target_title field (without index)
    title_render: titleFull, // Full title with index (e.g., "1.1 · Title")
    acf: {
      index: preview.index || '',
      status: '0',
      select_goal: goalIds,
    },
    computed: {
      target_semaphore_status: preview.assessment?.status?.category || null,
      target_status_score: preview.assessment?.status?.mean_score_0_100 ?? null,
      target_trend_status: preview.assessment?.trend?.category || null,
      target_trend_score: preview.assessment?.trend?.mean_score_0_5 ?? null,
    },
    goal_ids: goalIds,
    featured_image_url: '',
  };
}

/**
 * Transform indicator preview response to our Indicator format
 */
export function transformIndicatorPreview(preview: any): Indicator {
  const targetId = preview.target?.id || null;
  // Get goal_id from target's goal relationship if available
  // The target preview should have goal.id, but if not, we'll need to fetch it
  const goalId = preview.target?.goal?.id || preview.goal_id || null;
  
  // Get graph whole_data (keep as string for ChartContainer to parse)
  const graphWholeData = preview.graph?.whole_data || '';
  
  // Get series_description from datasets array (same as WordPress demo page)
  const seriesDescription = preview.datasets?.[0]?.series_description || '';
  const trimmedSeriesDesc = seriesDescription ? seriesDescription.trim() : '';
  
  const isBarChart = resolveIsBarChart(preview.graph?.isBarChart, graphWholeData);
  const chartType = chartTypeFromBarFlag(isBarChart);
  const dataDisplayType = dataDisplayTypeFromBarFlag(isBarChart);

  // Process chart payload - apply same logic as WordPress demo page
  let chartPayload = preview.graph?.payload || null;
  const payloadDatasets = chartPayload?.datasets;
  const hasPayloadDatasets = Array.isArray(payloadDatasets) && payloadDatasets.length > 0;
  if (!hasPayloadDatasets && graphWholeData) {
    // Fallback: build from whole_data when API returned empty datasets (e.g. legacy storage)
    const built = buildChartDataFromWholeData(graphWholeData);
    if (built) chartPayload = { type: chartType, ...built, calculationColumnIndex: 0 };
  } else if (chartPayload && chartPayload.type !== chartType) {
    chartPayload = { ...chartPayload, type: chartType };
  }
  // Keep chart series labels from the API (calculation column label, e.g. Total). Series description is metadata only — see chartSeriesDescription.

  // Debug logs removed - too verbose for 244+ indicators
  // Uncomment only when debugging specific indicator issues
  // if (preview.graph?.payload?.datasets && preview.graph.payload.datasets.length === 0) {
  //   console.log(`[${preview.id}] Empty datasets`);
  // }
  
  // Title from ACF (preview.title is now from ACF, not post_title)
  const title = stripLeadingIndicatorCode(decodeHtmlEntities(preview.title || ''), preview.code);
  const titleFull = indicatorTitleWithCode(
    decodeHtmlEntities(preview.title_full || preview.title || ''),
    preview.code
  );

  const chartGraphs = Array.isArray(preview.graphs) && preview.graphs.length > 0
    ? preview.graphs.map((g: any) => {
        const gWhole = typeof g?.whole_data === 'string' ? g.whole_data : '';
        const gBar = resolveIsBarChart(!!g?.isBarChart, gWhole);
        const gType = gBar ? 'bar' as const : 'line' as const;
        let gPayload = g?.payload || null;
        if (!gPayload?.datasets?.length && g?.whole_data) {
          const built = buildChartDataFromWholeData(g.whole_data);
          if (built) gPayload = { type: gType, ...built, calculationColumnIndex: 0 };
        } else if (gPayload && gPayload.type !== gType) {
          gPayload = { ...gPayload, type: gType };
        }
        return {
          chartType: gType,
          chartData: gPayload,
          seriesDescription: (g?.series_description || '').trim(),
          unitLabel: g?.unit_label || null,
          // Per-series display unit (multi-unit EU charts must not share assessment unit='%')
          unit: g?.unit || null,
          eurostatUnitCode: g?.eurostat_unit_code || null,
          isAssessmentSeries: !!g?.is_assessment_series,
          isBarChart: gBar,
        };
      })
        // Assessment Dataset Whole Data chart first (D-25 multi-unit)
        .sort(
          (a: { isAssessmentSeries?: boolean }, b: { isAssessmentSeries?: boolean }) =>
            Number(!!b.isAssessmentSeries) - Number(!!a.isAssessmentSeries)
        )
    : undefined;
  
  return {
    id: preview.id,
    title: title, // ACF title field (without code)
    title_render: titleFull, // Full title with code (e.g., "1.1.1 · Title")
    goal_id: goalId,
    target_id: targetId,
    acf: {
      index: preview.code || '',
      description: preview.description || '',
      source: preview.links?.source || '',
      reference_field: preview.links?.reference || '',
      data_display_type: dataDisplayType,
      graph_data_indicators: {
        data_display_type: dataDisplayType,
        whole_data: graphWholeData,
      },
      whole_data: graphWholeData,
      target_value: preview.configuration?.target_value ?? null,
      long_term_objective: preview.configuration?.target_value ?? null,
      has_numeric_target: preview.configuration?.has_numeric_target || false,
      indicator_type: preview.configuration?.indicator_type || preview.indicator_type || 'quantitative',
      normative_direction: preview.configuration?.normative_direction || null,
      select_targets: targetId ? [targetId] : [],
      metadata: preview.metadata || [], // Array of {key, value, is_link}
    },
    computed: {
      score: preview.assessment?.latest?.value ?? null,
      trend: preview.assessment?.trend?.category || null,
      trend_score: preview.assessment?.trend?.score_0_5 ?? null,
      semaphore_status: preview.assessment?.status?.category || null,
      status_score: preview.assessment?.status?.score_0_100 ?? null,
      trend_status: preview.assessment?.trend?.category || null,
      baseline: preview.assessment?.baseline || null,
      latest: preview.assessment?.latest || null,
      target: preview.assessment?.target || null,
      scores_by_series: {},
      eu_status_details: preview.assessment?.details?.eu_status || null,
      eu_data_status: preview.assessment?.details?.eu_data_status || null,
      availability_message: preview.assessment?.details?.availability_message || null,
      kosovo_vs_eu_percent: preview.assessment?.details?.eu_status?.kosovo_vs_eu_percent
        ?? preview.assessment?.status?.kosovo_vs_eu_percent
        ?? null,
    },
    chartType,
    chartData: chartPayload,
    chartGraphs,
    /** Indicator type: quantitative, ordinal, or binary (yes/no). Used for binary-specific UI (status block + timeline). */
    indicatorType: (preview.configuration?.indicator_type || preview.indicator_type || 'quantitative') as 'quantitative' | 'ordinal' | 'binary',
    /** EU-only editorial tag (D-2): none | national | proxy */
    indicatorTag: preview.indicator_tag || 'none',
    // Optional description of the dataset/series to display near charts
    chartSeriesDescription: trimmedSeriesDesc,
    // Optional unit and y-axis bounds from configuration
    unit: preview.configuration?.unit || null,
    yAxisMin: typeof preview.configuration?.y_axis_min === 'number' ? preview.configuration.y_axis_min : null,
    yAxisMax: typeof preview.configuration?.y_axis_max === 'number' ? preview.configuration.y_axis_max : null,
    moeEnabled: !!preview.configuration?.moe_enabled,
    moeMode: preview.configuration?.moe_mode || 'around',
    moePercent: typeof preview.configuration?.moe_percent === 'number' ? preview.configuration.moe_percent : null,
    featured_image_url: '',
  };
}

/**
 * Transform list endpoint response (minimal: id, title) to Goal format
 * Extract goal number from title to determine color and icon
 */
export function transformGoalFromList(listItem: {
  id: number;
  title: string;
  index?: string;
  featured_image_url?: string;
}): Goal {
  const title = listItem.title || '';
  const indexFromApi = (listItem.index || '').replace(/\D/g, '');
  // Extract goal number from title (e.g., "Goal 1:" -> "1") when list has no index
  const goalMatch = title.match(/Goal\s+(\d+)/i);
  const goalNumber = indexFromApi || (goalMatch ? goalMatch[1] : '');
  
  // Map goal numbers to colors (SDG color palette)
  const goalColors: Record<string, string> = {
    '1': '#E5243B', '2': '#DDA63A', '3': '#4C9F38', '4': '#C5192D', '5': '#FF3A21',
    '6': '#26BDE2', '7': '#FCC30B', '8': '#A21942', '9': '#FD6925', '10': '#DD1367',
    '11': '#FD9D24', '12': '#BF8B2E', '13': '#3F7E44', '14': '#0A97D9', '15': '#56C02B',
    '16': '#00689D', '17': '#19486A'
  };
  
  const color = goalNumber ? (goalColors[goalNumber] || '') : '';
  const featuredFromApi = (listItem.featured_image_url || '').trim();
  
  return {
    id: listItem.id,
    title: title,
    title_render: title,
    acf: {
      index: goalNumber,
      name: title,
      description: '',
      goal_color: color,
      status: '0',
      alignment_with_sdg_data: {
        status: '0'
      },
    },
    computed: {},
    color: color,
    icon: goalNumber ? `sdg-${goalNumber}.svg` : '',
    featured_image_url: featuredFromApi,
  };
}

/**
 * Transform list endpoint response (minimal: id, title) to Target format
 * Extract target index from title (e.g., "1.1 Title" -> "1.1")
 */
export function transformTargetFromList(listItem: { id: number; title: string }): Target {
  const title = listItem.title || '';
  // Extract target index from title (e.g., "1.1 Title" or "1.a Title" -> "1.1" or "1.a")
  const indexMatch = title.match(/^(\d+(?:\.\d+)?(?:\.\w+)?)/);
  const index = indexMatch ? indexMatch[1] : '';
  
  // Extract goal number from index (first number)
  const goalMatch = index.match(/^(\d+)/);
  const goalNumber = goalMatch ? parseInt(goalMatch[1]) : null;
  const goalIds = goalNumber ? [goalNumber] : [];
  
  return {
    id: listItem.id,
    title: title,
    title_render: title,
    acf: {
      index: index,
      status: '0',
    },
    computed: {},
    goal_ids: goalIds,
    featured_image_url: '',
  };
}

/**
 * Transform list endpoint response (minimal: id, title) to Indicator format
 * Extract indicator code from title (e.g., "1.1.1 Title" -> "1.1.1")
 */
export function transformIndicatorFromList(listItem: { id: number; title: string }): Indicator {
  const title = listItem.title || '';
  // Extract indicator code from title (e.g., "1.1.1 Title" -> "1.1.1")
  const codeMatch = title.match(/^(\d+(?:\.\d+)*(?:\.\w+)?)/);
  const code = codeMatch ? codeMatch[1] : '';
  
  // Extract goal number from code (first number)
  const goalMatch = code.match(/^(\d+)/);
  const goalNumber = goalMatch ? parseInt(goalMatch[1]) : null;
  
  return {
    id: listItem.id,
    title: title,
    title_render: title,
    goal_id: goalNumber,
    target_id: null,
    acf: {
      index: code,
      status: '0',
      description: '',
    },
    computed: {},
    featured_image_url: '',
  };
}


